import { useState, useCallback, useEffect } from 'react'
import type { WithdrawalRequest, WithdrawalStatus } from '../types'

const STORAGE_KEY = 'aerozeep_withdrawals'

export function useWithdrawals() {
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load withdrawals from storage
  const loadWithdrawals = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        setWithdrawals(JSON.parse(stored))
      }
    } catch (err) {
      console.error('Failed to load withdrawals', err)
    }
  }, [])

  // Save withdrawals to storage
  const saveWithdrawals = useCallback((newWithdrawals: WithdrawalRequest[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newWithdrawals))
      setWithdrawals(newWithdrawals)
      window.dispatchEvent(new Event('withdrawals-updated'))
    } catch (err) {
      console.error('Failed to save withdrawals', err)
    }
  }, [])

  // Request withdrawal
  const requestWithdrawal = useCallback(
    async (
      amount: number,
      bankDetails: {
        bankName: string
        bankCode: string
        accountNumber: string
        accountName: string
      },
    ): Promise<WithdrawalRequest> => {
      setIsLoading(true)
      setError(null)

      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1500))

        const newWithdrawal: WithdrawalRequest = {
          id: `WD-${Date.now()}`,
          userId: 'user-001',
          amount,
          bankDetails,
          status: 'pending',
          requestedAt: new Date().toISOString(),
        }

        const updated = [newWithdrawal, ...withdrawals]
        saveWithdrawals(updated)
        return newWithdrawal
      } catch (err) {
        setError('Failed to request withdrawal')
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [withdrawals, saveWithdrawals],
  )

  // Update withdrawal status (admin action)
  const updateWithdrawalStatus = useCallback(
    async (
      withdrawalId: string,
      status: WithdrawalStatus,
      notes?: string,
      failureReason?: string,
    ) => {
      setIsLoading(true)
      setError(null)

      try {
        await new Promise((resolve) => setTimeout(resolve, 1000))

        const updated = withdrawals.map((w) => {
          if (w.id === withdrawalId) {
            const updatedWithdrawal: WithdrawalRequest = {
              ...w,
              status,
              notes,
              failureReason,
            }

            if (status === 'reviewing' || status === 'approved') {
              updatedWithdrawal.reviewedAt = new Date().toISOString()
              updatedWithdrawal.reviewedBy = 'admin-001'
            }

            if (status === 'completed') {
              updatedWithdrawal.completedAt = new Date().toISOString()
            }

            return updatedWithdrawal
          }
          return w
        })

        saveWithdrawals(updated)
      } catch (err) {
        setError('Failed to update withdrawal')
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [withdrawals, saveWithdrawals],
  )

  // Get withdrawals by status
  const getWithdrawalsByStatus = useCallback(
    (status: WithdrawalStatus) => {
      return withdrawals.filter((w) => w.status === status)
    },
    [withdrawals],
  )

  // Get user withdrawals
  const getUserWithdrawals = useCallback(
    (userId: string) => {
      return withdrawals.filter((w) => w.userId === userId)
    },
    [withdrawals],
  )

  // Initialize on mount
  useEffect(() => {
    loadWithdrawals()

    const handleUpdate = () => loadWithdrawals()
    window.addEventListener('withdrawals-updated', handleUpdate)

    return () => {
      window.removeEventListener('withdrawals-updated', handleUpdate)
    }
  }, [loadWithdrawals])

  return {
    withdrawals,
    isLoading,
    error,
    requestWithdrawal,
    updateWithdrawalStatus,
    getWithdrawalsByStatus,
    getUserWithdrawals,
  }
}
